import Tour from '@core/components/Tour';
import { useReduxLoading } from '@core/hooks';
import { useApiUtils } from '@core/util/apiUtils';
import { comprasTour } from '@core/util/mockTourUtils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import { cancelarAlert, excluirAlert } from 'app/services/alerts';
import { compraApi } from 'app/services/api/compraApi';
import empresaApi from 'app/services/api/empresaApi';
import { relatorioApi } from 'app/services/api/relatorioApi';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { eventReactGA, initializeReactGA } from 'app/utils/analyticsUtil';
import { visualizarRelatorioV2 } from 'app/utils/enviromentUtil';
import { getEmpresaContexto } from 'app/utils/geralUtil';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import {
  CompraProdutoModal,
  CompraSearchHeader,
  CompraServicoModal,
  CompraTable,
  VisualizarCompraModal,
} from './components';
import CompraBuscaAvancadaModal from './components/modal/CompraBuscaAvancadaModal';
import { steps } from './components/steps/CompraSteps';
import { CompraContext, CompraProvider } from './CompraContext';

const COMPRA = 'Compra';

const Compra = () => {
  const [, setSelecao] = useContext(CompraContext).selecao;
  const [, setTipoSelecao] = useContext(CompraContext).tipoSelecao;
  const [, setCompras] = useContext(CompraContext).compras;
  const [, setCompraAtual] = useContext(CompraContext).compraAtual;
  const [, setTotalElements] = useContext(CompraContext).totalElements;
  const [, setModal] = useContext(CompraContext).modal;
  const [, setPage] = useContext(CompraContext).page;
  const [, setSize] = useContext(CompraContext).size;
  const [, setSort] = useContext(CompraContext).sort;
  const [filtroAvancado] = useContext(CompraContext).filtroAvancado;
  const [loading, setLoading] = useContext(CompraContext).loading;
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;
  const [empresaIbsCbs, setEmpresaIbsCbs] = useState(false);

  const [run, setRun] = useState(false);
  const [onTour, setOnTour] = useState(false);

  const history = useHistory();

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  useReduxLoading(loading);

  useEffect(() => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    buscarTour();
    // Efeito de montagem: roda uma única vez. Incluir as dependências reexecutaria a carga
    // inicial a cada render (loop de chamadas à API).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const carregarEmpresaIbsCbs = async () => {
      try {
        const empresaId = getEmpresaContexto();
        const empresa = await empresaApi.buscar(empresaId);
        if (empresa?.data?.ibsCbs) {
          setEmpresaIbsCbs(true);
        }
      } catch (error) {
        setEmpresaIbsCbs(false);
      }
    };
    carregarEmpresaIbsCbs();
  }, []);

  const showSuccess = (message) => {
    dispatch(Actions.showMessage({ message, variant: 'success' }));
  };

  const resetSelecao = () => {
    setSelecao([]);
    setTipoSelecao('NENHUM');
  };

  const initialPage = {
    page: 0,
    q: '',
    size: 15,
    sort: 'dataCompra,DESC',
  };

  const buscarTour = async () => {
    const funcionalidadeJaExistente = funcionalidadesTour?.some((e) => e === 'COMPRAS');
    if (!funcionalidadeJaExistente) {
      setRun(true);
      setOnTour(true);
      carregarDadosTour();
    } else {
      fetchCompras();
    }
  };

  const fetchCompra = async (produtoId) => {
    let response;
    setLoading(true);
    try {
      response = await compraApi().buscar(produtoId);
      setCompraAtual(response?.data);
    } catch (err) {
      apiUtils.handleApiError(err);
    }
    setLoading(false);
    return response?.data;
  };

  const carregarDadosTour = () => {
    setLoading(true);
    setSelecao([]);
    setPage(1);
    setSize(comprasTour.length);
    setTotalElements(3);
    setCompras(comprasTour);
    setLoading(false);
  };

  const fetchCompras = async (search = initialPage) => {
    try {
      setLoading(true);

      if (!search?.filtroAvancado) {
        search.filtroAvancado = filtroAvancado;
      }
      if (search.filtroAvancado.categoria?.id) {
        search.filtroAvancado.categoriaId = search.filtroAvancado.categoria.id;
      }

      if (search.filtroAvancado.fornecedor?.id) {
        search.filtroAvancado.fornecedorId = search.filtroAvancado.fornecedor.id;
      }

      if (search.filtroAvancado.produto?.id) {
        search.filtroAvancado.produtoId = search.filtroAvancado.produto.id;
      }

      const response = await compraApi().buscarTodos(search);
      setSelecao([]);
      setPage(response.data.number);
      setSize(response.data.size);
      setSort(search.sort);
      setTotalElements(response.data.totalElements);
      setCompras(response.data.content);
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectionChange = (selecao) => {
    setSelecao(selecao);
  };

  const handleSubmit = async (_compra, pageable, tipo) => {
    let res;
    let message;

    setLoading(true);

    try {
      if (_compra?.id) {
        eventReactGA(COMPRA, 'Atualizar compra');
        res = await compraApi(tipo).atualizar(_compra);
        message = 'Compra editada com sucesso. A conta a pagar referente à esta compra também será atualizada.';
      } else {
        eventReactGA(COMPRA, 'Cadastrar compra');
        res = await compraApi(tipo).criar(_compra);
        message = 'Compra salva com sucesso';
      }

      showSuccess(message);
      resetSelecao();
      fetchCompras(pageable);
    } catch (err) {
      if (err?.response?.data?.errorKey === 'compra.duplicada') {
        cancelarAlert({
          opcoes: {
            title:
              'Identificamos que já existe uma compra realizada com o mesmo fornecedor, data e valor total. Deseja continuar?',
            text: '',
          },
          confirmar: async () => {
            setLoading(true);
            try {
              if (_compra?.id) {
                res = await compraApi(tipo).atualizar(_compra, true);
                message = 'Compra editada com sucesso';
              } else {
                res = await compraApi(tipo).criar(_compra, true);
                message = 'Compra salva com sucesso';
              }
              setModal('');
              showSuccess(message);
              resetSelecao();
              fetchCompras();
            } catch (err) {
              apiUtils.handleApiError(err);
            }
            setLoading(false);
          },
        });
      } else {
        apiUtils.handleApiError(err);
      }
    }

    setLoading(false);

    return res;
  };

  const handleDelete = (filtro) => {
    excluirAlert({
      confirmar: async () => {
        try {
          setLoading(true);
          const compras = { ...filtro, ...filtroAvancado, ids: filtro.ids.map((p) => p.id) };
          eventReactGA(COMPRA, 'Excluir compra');
          await compraApi().excluir(compras);
          setLoading(false);

          showSuccess(filtro?.ids.length === 1 ? 'Compra excluída com sucesso' : 'Compras excluídas com sucesso');
          resetSelecao();
          fetchCompras();
        } catch (err) {
          apiUtils.handleApiError(err);
        }
        setLoading(false);
      },
    });
  };

  const onVisualizarPedido = async (compra) => {
    eventReactGA(COMPRA, 'Emitir pedido de compra');
    const response = await relatorioApi().salvarFiltroPedidoCompra(compra.id);
    visualizarRelatorioV2(response.data);
    // visualizarRelatorio(compra.id, 'compra/pedido', `titulo=compra_${compra.compraId}`);
  };

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('COMPRAS');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setOnTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      fetchCompras();
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Tour
        steps={steps}
        run={run}
        setRun={setRun}
        disableScrolling={true}
        onFinish={() => {
          atualizarTour();
        }}
      />
      <FusePageCarded
        innerScroll
        header={<CompraSearchHeader />}
        content={
          <>
            <CompraTable
              onSelection={handleSelectionChange}
              fetchCompra={fetchCompra}
              fetchCompras={fetchCompras}
              onDelete={handleDelete}
              onVisualizarPedido={onVisualizarPedido}
              onTour={onTour}
            />
            <CompraProdutoModal onSubmit={handleSubmit} empresaIbsCbs={empresaIbsCbs} />
            <CompraServicoModal onSubmit={handleSubmit} empresaIbsCbs={empresaIbsCbs} />
            <VisualizarCompraModal onVisualizarPedido={onVisualizarPedido} />
            <CompraBuscaAvancadaModal fetchCompras={fetchCompras} />
          </>
        }
      />
    </>
  );
};

const CompraContainer = () => (
  <CompraProvider>
    <Compra />
  </CompraProvider>
);

export default CompraContainer;
